<template>
  <div>
    <div
      class="relative border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center transition-all duration-200 ease-in-out group"
      :class="{
        'border-primary-500 bg-primary-50 dark:bg-primary-900/10 ring-2 ring-primary-500 ring-opacity-50': isDragging,
        'cursor-pointer hover:border-primary-500 hover:bg-primary-50 dark:hover:border-primary-400 dark:hover:bg-primary-900/10': !props.disabled,
        'opacity-50 cursor-not-allowed': props.disabled
      }"
      @dragover.prevent="props.disabled ? null : handleDragOver()"
      @dragleave.prevent="props.disabled ? null : handleDragLeave()"
      @drop.prevent="props.disabled ? null : handleDrop($event)"
      @click="props.disabled ? null : openFileDialog()"
    >
      <input ref="fileInput" type="file" multiple class="hidden" @change="handleFileSelect" :disabled="props.disabled">
      <div class="flex flex-col items-center justify-center gap-2">
        <div class="p-3 bg-gray-100 dark:bg-gray-800 rounded-full group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors duration-200 mb-1">
          <UIcon name="i-heroicons-cloud-arrow-up" class="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 dark:group-hover:text-primary-400" />
        </div>
        <div class="space-y-1">
          <p class="text-sm font-medium text-gray-700 dark:text-gray-200" v-html="t('fileUpload.dragAndDrop')"></p>
          <p class="text-xs text-gray-500 dark:text-gray-400" v-html="t('fileUpload.fileSizeHint', { maxSize: MAX_FILE_SIZE_MB, allowedTypes: ALLOWED_EXTENSIONS.join(', ') })"></p>
        </div>
      </div>
    </div>
    
    <div v-if="files.length > 0" class="mt-4 space-y-2">
      <div v-for="(file, index) in files" :key="file.name + index" class="flex justify-between items-center p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm">
        <div class="flex items-center gap-3 min-w-0">
          <div class="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center" :class="getFileTypeInfo(file.type).bg">
            <UIcon :name="getFileTypeInfo(file.type).icon" class="text-lg" :class="getFileTypeInfo(file.type).color" />
          </div>
          <span class="text-sm font-medium text-gray-700 dark:text-gray-200 truncate" :title="file.name">{{ file.name }}</span>
        </div>
        <div class="flex items-center gap-3 flex-shrink-0">
          <div class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 dark:bg-gray-700/30 text-xs text-gray-500 dark:text-gray-400">
            {{ formatFileSize(file.size) }}
          </div>
          <UButton
            variant="ghost"
            color="gray"
            size="xs"
            icon="i-heroicons-x-mark"
            @click.stop="removeFile(index)"
            :title="t('fileUpload.removeFile')"
            class="hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition-colors"
          />
        </div>
      </div>
      <UButton @click="uploadFiles" block icon="i-heroicons-arrow-up-tray-solid" color="primary" class="mt-3 cursor-pointer text-white!">
        {{ t('fileUpload.upload', { count: files.length }) }}
      </UButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatFileSize, getFileTypeInfo } from '@/utils/fileUtils';
import { showError } from '@/utils/utils.ts';

const props = defineProps<{
  disabled?: boolean;
}>();

const { t } = useI18n();
const emit = defineEmits(['files-uploaded']);

const MAX_FILES_PER_UPLOAD = 10;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'jpg', 'jpeg', 'png', 'zip'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
  'image/jpeg',
  'image/png',
  'application/zip',
  'application/x-zip-compressed'
];

const isDragging = ref(false);
const files = ref<File[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);

const handleDragOver = () => {
  isDragging.value = true;
}

const handleDragLeave = () => {
  isDragging.value = false;
}

const handleDrop = (event: DragEvent) => {
  isDragging.value = false;
  const droppedFiles = event.dataTransfer?.files;
  
  if (droppedFiles) {
    addFiles(Array.from(droppedFiles));
  }
}

const handleFileSelect = (event: Event) => {
  const selectedFiles = (event.target as HTMLInputElement).files;
  
  if (selectedFiles) {
    addFiles(Array.from(selectedFiles));
  }
  
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

const addFiles = (newFiles: File[]) => {
  const currentCount = files.value.length;
  const totalAfterAdd = currentCount + newFiles.length;
  
  if (totalAfterAdd > MAX_FILES_PER_UPLOAD) {
    showError(t('fileUpload.maxFilesExceeded', { max: MAX_FILES_PER_UPLOAD }));
    return;
  }
  
  const validFiles = newFiles.filter(file => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showError(t('fileUpload.fileSizeExceeded', { fileName: file.name, maxSize: MAX_FILE_SIZE_MB }));
      return false;
    }
    
    const extension = file.name.split('.').pop()?.toLowerCase();
    const isAllowedExtension = extension ? ALLOWED_EXTENSIONS.includes(extension) : false;
    const isAllowedMime = file.type ? ALLOWED_MIME_TYPES.includes(file.type) : false;
    
    if (!isAllowedExtension && !isAllowedMime) {
      showError(t('fileUpload.fileTypeNotSupported', { fileName: file.name, allowedTypes: ALLOWED_EXTENSIONS.join(', ') }));
      return false;
    }
    
    return true;
  });
  
  const uniqueNewFiles = validFiles.filter(newFile => !files.value.some(existingFile => existingFile.name === newFile.name && existingFile.size === newFile.size));
  files.value = [...files.value, ...uniqueNewFiles];
}

const openFileDialog = () => {
  fileInput.value?.click();
}

const removeFile = (index: number) => {
  files.value.splice(index, 1);
}

const uploadFiles = () => {
  if (files.value.length === 0) return;
  
  emit('files-uploaded', files.value);
  files.value = [];
}
</script>